import { useState } from 'react'
import VideoEmbed, { getYoutubeThumbnail } from './VideoEmbed'
import axios from 'axios'

const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8001',
  timeout: 5000,
})

// Formata com até 2 casas decimais, sem zeros à direita (213.50 -> 213.5, 213.00 -> 213)
const fmtPeso = (v) => {
  const n = Number(v)
  return (Math.round(n * 100) / 100).toString()
}

const fmtBRL = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`

export default function LoteCard({ lote, config, corPrimaria, corBotaoInteresse, whatsappContatos, slug }) {
  const {
    numero_lote,
    tipo_animal,
    raca,
    sexo,
    idade,
    observacoes,
    fazenda,
    peso,
    qtd,
    qtd_crias_femeas,
    qtd_crias_machos,
    link_video,
    video_url,
    video_poster_url,
    animais,
    valor_minimo
  } = lote

  // Fluxo cavalo: lote sem tipo/raça/sexo próprios, tem N animais em
  // `lote.animais` — cada um com seus dados. Card vira um carrossel entre
  // eles (1 lote = 1 card, sempre — não expande em vários cards).
  const ehCavalo = Array.isArray(animais) && animais.length > 0
  const [animalIdx, setAnimalIdx] = useState(0)
  const animalAtual = ehCavalo ? animais[Math.min(animalIdx, animais.length - 1)] : null
  const proximoAnimal = () => setAnimalIdx((i) => (i + 1) % animais.length)
  const anteriorAnimal = () => setAnimalIdx((i) => (i - 1 + animais.length) % animais.length)
  // Destaque: animal vendido com % != 100 (não é o animal inteiro à venda)
  const pctVendaNum = animalAtual?.percentual_venda != null ? parseFloat(animalAtual.percentual_venda) : null
  const percentualParcial = pctVendaNum != null && Math.abs(pctVendaNum - 100) > 0.01
    ? (Number.isInteger(pctVendaNum) ? pctVendaNum : parseFloat(pctVendaNum.toFixed(1)))
    : null

  const [showContatosModal, setShowContatosModal] = useState(false)
  const [lightboxMedia, setLightboxMedia] = useState(null) // 'foto' | 'video' | null

  // Mídia do animal atual (fluxo cavalo) — foto e vídeo ficam lado a lado,
  // clique em qualquer um abre em destaque (lightbox).
  const fotoAnimalUrl = animalAtual?.foto_url || null
  const videoAnimalLinkUrl = ehCavalo ? (animalAtual?.link_video || link_video) : null
  const videoAnimalFileUrl = ehCavalo ? (animalAtual?.video_url || video_url) : null
  const videoAnimalPosterUrl = ehCavalo ? (animalAtual?.video_poster_url || video_poster_url) : null
  const temVideoAnimal = !!(videoAnimalFileUrl || videoAnimalLinkUrl)
  const videoAnimalThumbUrl = videoAnimalPosterUrl || (videoAnimalLinkUrl ? getYoutubeThumbnail(videoAnimalLinkUrl) : null)
  const quantidadeLabel = ehCavalo
    ? (animais.length === 1 ? (animalAtual?.nome || '') : `${animais.length} animais`)
    : (qtd ? `${qtd} cabeça${qtd > 1 ? 's' : ''}` : '')
  // O % vai destacado na frente do nome quando é 1 animal só; com mais de 1
  // no lote, vira um badge separado (o carrossel troca de animal e o % pode
  // mudar a cada slide, não dá pra grudar no texto do header).
  const mostrarPercentualNoNome = ehCavalo && animais.length === 1 && percentualParcial
  const mostrarBadgePercentual = ehCavalo && animais.length > 1 && percentualParcial

  const mensagem = ehCavalo
    ? `Olá! Tenho interesse no lote ${numero_lote} - ${animalAtual?.nome || ''}${animalAtual?.raca ? ` (${animalAtual.raca})` : ''}`
    : `Olá! Tenho interesse no lote ${numero_lote} - ${tipo_animal} ${raca || ''} (${quantidadeLabel})`

  const handleInteresse = () => {
    // Registrar interesse (fire-and-forget)
    if (slug) {
      publicApi.post(`/api/catalogo-online/${slug}/interesse`, {
        lote_id: lote.id,
        numero_lote: numero_lote,
        contato_nome: whatsappContatos?.length === 1 ? whatsappContatos[0].nome : '',
      }).catch(() => {})
    }

    if (!whatsappContatos || whatsappContatos.length === 0) {
      window.open(`https://wa.me/?text=${encodeURIComponent(mensagem)}`, '_blank')
    } else if (whatsappContatos.length === 1) {
      const numero = whatsappContatos[0].numero.replace(/\D/g, '')
      window.open(`https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`, '_blank')
    } else {
      setShowContatosModal(true)
    }
  }

  const handleSelecionarContato = (contato) => {
    // Registrar interesse com nome do contato
    if (slug) {
      publicApi.post(`/api/catalogo-online/${slug}/interesse`, {
        lote_id: lote.id,
        numero_lote: numero_lote,
        contato_nome: contato.nome,
      }).catch(() => {})
    }
    const numero = contato.numero.replace(/\D/g, '')
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`, '_blank')
    setShowContatosModal(false)
  }

  return (
    <>
      <div className={`bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow ${percentualParcial ? 'border-amber-400 ring-2 ring-amber-300' : 'border-gray-200'}`}>
        {/* Header do lote — barra colorida */}
        <div
          className="px-4 md:px-6 py-3"
          style={{ backgroundColor: lote.status === 'FATURADO' ? '#16a34a' : lote.status === 'DEFESA' ? '#dc2626' : corPrimaria }}
        >
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-white font-black text-xl md:text-2xl flex-shrink-0">{numero_lote}</span>
              <span className="text-white/60 text-lg flex-shrink-0">–</span>
              <span className="text-base md:text-lg uppercase truncate min-w-0 flex-1">
                {mostrarPercentualNoNome && (
                  <span className="text-amber-300 font-black">{percentualParcial}% </span>
                )}
                <span className="text-white/90 font-medium">{quantidadeLabel}</span>
              </span>
              {lote.status === 'FATURADO' && (
                <span className="px-2 py-0.5 bg-white/20 text-white text-[11px] font-bold rounded-full uppercase border border-white/40 flex-shrink-0">
                  Vendido
                </span>
              )}
              {lote.status === 'DEFESA' && (
                <span className="px-2 py-0.5 bg-white/20 text-white text-[11px] font-bold rounded-full uppercase border border-white/40 flex-shrink-0">
                  Defesa
                </span>
              )}
              {mostrarBadgePercentual && (
                <span className="px-2 py-0.5 bg-amber-400 text-amber-950 text-[11px] font-bold rounded-full uppercase flex-shrink-0 whitespace-nowrap">
                  {percentualParcial}%
                </span>
              )}
            </div>
            {/* Fazenda: no canto, na mesma linha, a partir do desktop — no mobile desce pra linha de baixo */}
            {config.exibir_fazenda && fazenda && (
              <span className="hidden md:block text-white/90 text-xs md:text-sm font-semibold uppercase tracking-wide text-right max-w-[40%] truncate flex-shrink-0">
                {fazenda}
              </span>
            )}
          </div>
          {config.exibir_fazenda && fazenda && (
            <div className="md:hidden text-white/80 text-xs font-semibold uppercase tracking-wide truncate mt-0.5">
              {fazenda}
            </div>
          )}
        </div>

        {/* Corpo do card */}
        <div className="px-4 md:px-6 py-4">
          {ehCavalo ? (
            <>
              {/* Nome do animal só quando há mais de 1 (com 1 só, o nome já
                  está no cabeçalho do card, não repete aqui) + navegação do
                  carrossel. */}
              {animais.length > 1 && (
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h3 className="font-bold text-gray-900 text-base md:text-lg uppercase truncate">
                    {percentualParcial && (
                      <span className="font-semibold" style={{ color: corPrimaria }}>{percentualParcial}% </span>
                    )}
                    {animalAtual.nome}
                  </h3>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={anteriorAnimal}
                      aria-label="Animal anterior"
                      className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                      ‹
                    </button>
                    <span className="text-xs text-gray-400 font-medium tabular-nums">{animalIdx + 1}/{animais.length}</span>
                    <button
                      type="button"
                      onClick={proximoAnimal}
                      aria-label="Próximo animal"
                      className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}

              {/* Foto e vídeo do animal — lado a lado, clique abre em destaque */}
              {(fotoAnimalUrl || temVideoAnimal) && (
                <div className={`grid gap-3 mb-3 ${fotoAnimalUrl && temVideoAnimal ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {fotoAnimalUrl && (
                    <button
                      type="button"
                      onClick={() => setLightboxMedia('foto')}
                      className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-100 group"
                    >
                      <img
                        src={fotoAnimalUrl}
                        alt={animalAtual.nome}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </button>
                  )}
                  {temVideoAnimal && (
                    <button
                      type="button"
                      onClick={() => setLightboxMedia('video')}
                      className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-800 group"
                    >
                      {videoAnimalThumbUrl && (
                        <img
                          src={videoAnimalThumbUrl}
                          alt="Vídeo do animal"
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <svg className="w-5 h-5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {/* Grid de informações do animal */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                <InfoItem label="Raça" value={animalAtual.raca ? animalAtual.raca.toUpperCase() : '—'} />
                <InfoItem label="Sexo" value={animalAtual.sexo ? animalAtual.sexo.toUpperCase() : '—'} />
                <InfoItem label="Idade" value={animalAtual.idade ? animalAtual.idade.toUpperCase() : '—'} />
                <InfoItem label="Pelagem" value={animalAtual.pelagem ? animalAtual.pelagem.toUpperCase() : '—'} />
                <InfoItem label="Registro" value={animalAtual.registro || '—'} />
                {animalAtual.localizacao && (
                  <InfoItem label="Localização" value={animalAtual.localizacao.toUpperCase()} />
                )}
              </div>

              {(animalAtual.pai || animalAtual.mae) && (
                <div className="mt-3">
                  <span className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">Genealogia</span>
                  <div className="mt-1.5 flex flex-col sm:flex-row gap-2 items-stretch">
                    <div className="flex-1 min-w-0 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                      <span className="block text-[10px] font-bold uppercase tracking-wide text-blue-400">Pai</span>
                      <span className="block text-sm font-semibold text-blue-900 truncate">{animalAtual.pai ? animalAtual.pai.toUpperCase() : '—'}</span>
                      {animalAtual.pai_registro && (
                        <span className="block text-[11px] text-blue-500 mt-0.5 truncate">Reg. {animalAtual.pai_registro}</span>
                      )}
                    </div>
                    <span className="self-center text-gray-300 font-bold text-sm">×</span>
                    <div className="flex-1 min-w-0 bg-pink-50 border border-pink-100 rounded-lg px-3 py-2">
                      <span className="block text-[10px] font-bold uppercase tracking-wide text-pink-400">Mãe</span>
                      <span className="block text-sm font-semibold text-pink-900 truncate">{animalAtual.mae ? animalAtual.mae.toUpperCase() : '—'}</span>
                      {animalAtual.mae_registro && (
                        <span className="block text-[11px] text-pink-500 mt-0.5 truncate">Reg. {animalAtual.mae_registro}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Informações do animal (equivalente às observações do gado) */}
              {config.exibir_observacoes && animalAtual.informacoes && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <span className="font-semibold text-gray-500 text-xs uppercase tracking-wide">Obs: </span>
                    {animalAtual.informacoes}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Grid de informações */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                <InfoItem label="Tipo" value={tipo_animal ? tipo_animal.toUpperCase() : '—'} />
                <InfoItem label="Raça" value={raca ? raca.toUpperCase() : '—'} />
                <InfoItem label="Sexo" value={sexo ? sexo.toUpperCase() : '—'} />
                <InfoItem label="Idade" value={idade ? idade.toUpperCase() : '—'} />
                {config.exibir_peso_quantidade && (
                  <>
                    <InfoItem label="Peso" value={peso ? (config.modo_peso === 'peso' ? `${fmtPeso(peso)}kg` : `${fmtPeso(peso)}kg / ${fmtPeso(Number(peso) / 30)}@`) : '—'} />
                    <InfoItem label="Qtd" value={qtd || '—'} />
                    {(qtd_crias_femeas > 0 || qtd_crias_machos > 0) && (
                      <InfoItem
                        label="Crias"
                        value={[
                          qtd_crias_femeas > 0 ? `${qtd_crias_femeas} F` : null,
                          qtd_crias_machos > 0 ? `${qtd_crias_machos} M` : null,
                        ].filter(Boolean).join(' / ')}
                      />
                    )}
                  </>
                )}
              </div>

              {/* Observações */}
              {config.exibir_observacoes && observacoes && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <span className="font-semibold text-gray-500 text-xs uppercase tracking-wide">Obs: </span>
                    {observacoes}
                  </p>
                </div>
              )}

              {/* Video */}
              {(video_url || link_video) && (
                <div className="mt-4">
                  <VideoEmbed url={link_video} videoUrl={video_url} posterUrl={video_poster_url} />
                </div>
              )}
            </>
          )}

          {/* Valor mínimo + botão WhatsApp */}
          {(valor_minimo != null || (whatsappContatos && whatsappContatos.length > 0)) && (
            <div className="mt-4 flex items-center justify-center md:justify-end gap-3">
              {valor_minimo != null && (
                <div className="text-right">
                  <span className="block text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Valor mínimo</span>
                  <span className="block text-base font-bold text-gray-900">{fmtBRL(valor_minimo)}</span>
                </div>
              )}
              {whatsappContatos && whatsappContatos.length > 0 && (
                <button
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold transition-all hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] shadow-sm flex-shrink-0"
                  style={{ backgroundColor: corBotaoInteresse || '#25D366' }}
                  onClick={handleInteresse}
                >
                  <WhatsAppIcon />
                  Tenho Interesse!
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de seleção de contato */}
      {showContatosModal && (
        <ContatosModal
          contatos={whatsappContatos}
          onSelect={handleSelecionarContato}
          onClose={() => setShowContatosModal(false)}
          corPrimaria={corPrimaria}
        />
      )}

      {/* Lightbox de foto/vídeo do animal */}
      {lightboxMedia && (
        <MediaLightbox
          type={lightboxMedia}
          nome={animalAtual?.nome}
          fotoUrl={fotoAnimalUrl}
          videoLinkUrl={videoAnimalLinkUrl}
          videoFileUrl={videoAnimalFileUrl}
          videoPosterUrl={videoAnimalPosterUrl}
          onClose={() => setLightboxMedia(null)}
        />
      )}
    </>
  )
}

function MediaLightbox({ type, nome, fotoUrl, videoLinkUrl, videoFileUrl, videoPosterUrl, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 px-4 py-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar"
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors text-2xl leading-none"
      >
        ×
      </button>
      <div className="w-full max-w-2xl">
        {type === 'foto' ? (
          <img src={fotoUrl} alt={nome} className="w-full max-h-[80vh] object-contain rounded-lg" />
        ) : (
          <VideoEmbed url={videoLinkUrl} videoUrl={videoFileUrl} posterUrl={videoPosterUrl} autoLoad={true} />
        )}
      </div>
    </div>
  )
}

function ContatosModal({ contatos, onSelect, onClose, corPrimaria }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100" style={{ backgroundColor: corPrimaria }}>
          <h3 className="text-white font-bold text-lg">Falar com quem?</h3>
          <p className="text-white/70 text-sm">Selecione um contato</p>
        </div>

        {/* Lista de contatos */}
        <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
          {contatos.map((contato, idx) => (
            <button
              key={idx}
              onClick={() => onSelect(contato)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-green-50 hover:border-green-300 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <WhatsAppIcon className="text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{contato.nome}</p>
                <p className="text-gray-500 text-xs">{formatPhone(contato.numero)}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-2 text-sm text-gray-600 font-medium hover:text-gray-800 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}

function formatPhone(numero) {
  const clean = numero.replace(/\D/g, '')
  if (clean.length === 13) {
    // 55 34 99999 9999
    return `(${clean.slice(2, 4)}) ${clean.slice(4, 9)}-${clean.slice(9)}`
  }
  if (clean.length === 11) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`
  }
  return numero
}

function InfoItem({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">{label}</span>
      <span className="text-gray-900 font-bold text-sm md:text-base mt-0.5">{value}</span>
    </div>
  )
}

function WhatsAppIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}
